import React, {useEffect, useMemo, useState} from "react";
import {useLocation, useSearchParams} from "react-router-dom";
import {Collapse} from 'antd';
import UserDiscipline from "../../models/UserDiscipline";
import {
    BrandsFilter,
    DisciplinesFilter,
    ExperienceSelectFilter,
    IndustriesFilter,
    ProjectTemplatesFilter, RoleTemplatesFilter,
    SkillsFilter,
    TalentLocationFilter,
    TalentPoolsFilter
} from "./SearchTalentFilter";
import {useAuth} from "../../providers/AuthProvider";
import ProjectVisibilitySettingsInfo from "../projects/ProjectVisibilitySettingsInfo";


const ExpLevelOptions = TB.map(laravel.talent_experience_levels, (config, value) => ({
    label: `${config?.level} (${config?.label})`,
    value,
    isDefault: config.level === 1,
    level: config.level
}));

const BrandOptions = TB.map([...(laravel.talent_brands || [])], brand => ({
    label: (
        <span className="flex flex-row items-center justify-between gap-1.5">
            {/*{brand.icon_path ? (*/}
            {/*    <img alt={undefined} src={brand.icon_path} className="w-6 h-6"/>*/}
            {/*) : <PictureOutlined className="w-6 h-6" style={{fontSize: 8}}/>}*/}
            {brand?.name} {brand.domains?.length > 0 && <span className="text-info text-xxs">{TB.first(brand.domains).domain}</span>}
        </span>
    ),
    value: brand.id,
    ...brand,
    name: (
        <span className="flex flex-row items-center justify-between gap-1.5">
            {/*{brand.icon_path ? (*/}
            {/*    <img alt={undefined} src={brand.icon_path} className="w-5 h-5"/>*/}
            {/*) : <PictureOutlined className="w-6 h-6" style={{fontSize: 8}}/>}*/}
            <span className="whitespace-nowrap overflow-hidden">{brand?.name}</span>
            {brand.domains?.length > 0 && <span className="text-info text-xxs flex-grow text-ellipsis whitespace-nowrap overflow-hidden">{TB.first(brand.domains).domain}</span>}
        </span>
    ),

}));

const ImportanceLevelOptions = TB.map(laravel.importance_levels, (label, value) => ({
    label,
    value,
    isDefault: value === 2,
    level: value
}))


const disciplineCategories = TB.map([...laravel.discipline_categories], cat => {
    let option = TB.pick(cat, ['id', 'name', 'disciplines', 'display_order'])
    let disciplines = option.disciplines;

    option.disciplines = TB.map(disciplines, discipline => TB.pick(discipline, ['id', 'name', 'display_order']))
    option.value = option.id;

    return {
        ...option,
        options: option.disciplines.map(discipline => {
            return {
                ...discipline,
                value: discipline.id
            }
        })
    }
});

// console.log(disciplineCategories, 'DISPLICE CATS')

const AllDisciplines = TB.flatMap(disciplineCategories, 'options')
const allSkills = TB.uniqBy(UserDiscipline.skillsForDisciplines(TB.map(AllDisciplines, 'id')), 'id');

const templateCategories = TB.map([...laravel.project_template_categories], cat => {
    let option = TB.pick(cat, ['id', 'name', 'project_templates', 'display_order'])
    let project_templates = TB.filter(option.project_templates, {'is_valid': true, 'is_active': true});

    option.project_templates = TB.map(project_templates, template => TB.pick(template, ['id', 'name', 'display_order']))
    option.value = option.id;

    return {
        ...option,
        options: option.project_templates.map(template => {
            return {
                ...template,
                value: template.id
            }
        })
    }
});

const AllProjectTemplates = TB.flatMap(templateCategories, 'options')

const industries = TB.map(laravel.client_industries, (name, value) => {
    return {name, value: Number(value), id: Number(value)}
});


const expMapping = {
    80: 'junior',
    95: 'intermediate',
    100: 'senior',
}
const experienceLevels = TB.map(laravel.talent_experience_levels, (exp, value) => {
    return {
        ...exp,
        value: expMapping[parseInt(value)],
        name: exp.label + " (" + exp.level + ")",
        display: (
            <span>
                Min Experience:&nbsp;({exp.level})
            </span>
        )
    }
})

const defaultFilterOptions = {
    disciplineOptions: disciplineCategories,
    projectTemplateOptions: templateCategories,
    locationOptions: laravel.project_visibility_location_settings,
    skillOptions: allSkills,
    industryOptions: industries,
    experienceOptions: experienceLevels,
    brandOptions: BrandOptions
}
// console.log(industries, 'Industries Industries')

const DEFAULT_FILTERS = {
    favorited: 0,
    invited: 0,
    hired: 0,
    categories: [],
    project_templates: [],
    disciplines: [],
    experience: {
        junior: false,
        intermediate: false,
        senior: false,
    },
    location:   TB.locationGlobalSetting(),
    industries: [],
    skills: [],
    pool_ids: [],
}

const DEFAULT_SEARCH = {
    suggested: [],
    text: '',
}

const SearchTalentSidebar = ({
                                 forPosition,
                                 openOnMount = true,
                                 resetOnMount = false,
                                 enableTeams,
                                 searchEnabled = true,
                                 disabledFilters = [],
                                 isDisabled,
                                 useLocalStorage = false,
                                 title = "",
                                 defaultFilters = {disciplines: AllDisciplines},
                                 availableFilters = defaultFilterOptions,
                                 ...props
}) => {
    const [localFilters, setLocalFilters] = useState(defaultFilters);
    const {client_org_regions, auth} = useAuth();

    const [collapseActiveKeys, setCollapseActiveKeys] = useState(TB.isArray(openOnMount) ? openOnMount : []);

    const {pathname} = useLocation()
    const [searchParams, setSearchParams] = useSearchParams();

    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {

        const currentParams = Object.fromEntries([...searchParams]);

        const localFiltersNew = {...localFilters};
        let setupParams = async () => {
            let updatedFilters = {...DEFAULT_FILTERS,...defaultFilters};
            if (forPosition) {
                updatedFilters = {...defaultFilters}
            }
            await setLocalFilters(updatedFilters);
            return handleSearch(updatedFilters)
        };
        let isParamsValid = Object.keys(currentParams).length > 0;

        if (enableTeams) {

            let selectedTemplates = currentParams['project_templates'];
            if (selectedTemplates) {
                try {
                    selectedTemplates = JSON.parse(selectedTemplates);
                    let defaultTemplateIds = TB.map(defaultFilters.project_templates, (tem) => parseInt(tem.id))
                    isParamsValid = selectedTemplates?.length > 0 && !TB.filter(selectedTemplates, tem => !defaultTemplateIds?.includes(parseInt(tem)))?.length;

                    isParamsValid = selectedTemplates?.length > 0;
                }catch (e) {
                    console.warn(`Failed to parse template category: ${selectedTemplates}`, e);
                }
            }else isParamsValid = false;
        }else {
            let selectedRoles = currentParams[forPosition ? 'roles': 'disciplines'];
            if (selectedRoles) {
                try {
                    selectedRoles = JSON.parse(selectedRoles);
                    let availableDisciplineIds = TB.flatMap(availableFilters[forPosition ? 'roleOptions' : 'disciplineOptions'], option => {
                        if (option.options) {
                            return TB.map(option.options, 'id')
                        }
                        return option.id
                    })

                    isParamsValid = selectedRoles?.length > 0 && !TB.filter(selectedRoles, dis => !availableDisciplineIds?.includes(parseInt(dis)))?.length;

                }catch (e) {
                    console.warn(`Failed to parse role category: ${selectedRoles}`, e);
                }
            }else if (!forPosition) {
                isParamsValid = false
            }
        }
        // console.log('defaultFilters', {isParamsValid, localFiltersNew, localFilters})

        // Check if params are empty or missing required filters
        if (isParamsValid && !resetOnMount) {
            TB.map(currentParams, (value, key) => {

                if (key.includes('disciplines')) {
                    let roleIds = JSON.parse(value) || [];

                    roleIds = TB.filter(roleIds, role => !TB.isNaN(parseInt(role)))

                    roleIds = TB.map(roleIds, role => parseInt(role));

                    let availableDisciplines = TB.flatMap(availableFilters.disciplineOptions, option => {
                        if (option.options) {
                            return option.options
                        }
                        return option
                    })

                    let roles = TB.filter(availableDisciplines || [], dis => roleIds.includes(dis.id));
                    localFiltersNew.disciplines = TB.uniqBy([
                        ...(localFiltersNew.disciplines || []),
                        ...roles
                    ], 'id')
                }
                if (key.includes('roles')) {
                    let roleIds = JSON.parse(value) || [];

                    roleIds = TB.filter(roleIds, role => !TB.isNaN(parseInt(role)))

                    roleIds = TB.map(roleIds, role => parseInt(role));

                    let availableRoles = TB.flatMap(availableFilters.roleOptions, option => {
                        if (option.options) {
                            return option.options
                        }
                        return option
                    })

                    let roles = TB.filter(availableRoles || [], dis => roleIds.includes(dis.id));
                    localFiltersNew.roles = TB.uniqBy([
                        ...(localFiltersNew.roles || []),
                        ...roles
                    ], 'id')
                }
                if (key.includes('project_templates')) {
                    let templateIds = JSON.parse(value) || [];

                    templateIds = TB.filter(templateIds, role => !TB.isNaN(parseInt(role)));
                    templateIds = TB.map(templateIds, template => parseInt(template))
                    let availableTemplates = TB.flatMap(availableFilters.projectTemplateOptions, option => {
                        if (option.options) {
                            return option.options
                        }
                        return option
                    })
                    let project_templates = TB.filter(availableTemplates || [], tem => templateIds.includes(tem.id));
                    localFiltersNew.project_templates = TB.uniqBy([
                        ...(localFiltersNew.project_templates || []),
                        ...project_templates
                    ], 'id')
                }
                if (key.includes('skills')) {
                    let skillIds = JSON.parse(value) || [];
                    skillIds = TB.filter(skillIds, skill => !TB.isNaN(parseInt(skill)))
                    skillIds = TB.map(skillIds, skill => parseInt(skill))

                    let skills = TB.filter(availableFilters.skillOptions || [], skill => skillIds.includes(skill.id));

                    let scorePrefix = TB.replace(key, '_skills', '');
                    if (scorePrefix && TB.find(ImportanceLevelOptions, level => TB.slugify(level.label) === TB.slugify(scorePrefix))) {
                        skills = TB.map(skills, skill => ({...skill, importance: TB.find(ImportanceLevelOptions, level => TB.slugify(level.label) === TB.slugify(scorePrefix))?.value}));
                    }
                    localFiltersNew.skills = TB.uniqBy([
                        ...(localFiltersNew.skills || []),
                        ...skills
                    ], 'id')
                }

                if (key.includes('industries')) {
                    let industriesIds = JSON.parse(value) || [];

                    industriesIds = TB.filter(industriesIds, industry => !TB.isNaN(parseInt(industry)))
                    industriesIds = TB.map(industriesIds, industry => parseInt(industry))

                    let industriesSearch = TB.filter(availableFilters.industryOptions || [], industry => industriesIds.includes(industry.id));

                    let scorePrefix = TB.replace(key, '_industries', '');
                    if (scorePrefix && TB.find(ImportanceLevelOptions, level => TB.slugify(level.label) === TB.slugify(scorePrefix))) {
                        industriesSearch = TB.map(industriesSearch, ind => ({...ind, importance: TB.find(ImportanceLevelOptions, level => TB.slugify(level.label) === TB.slugify(scorePrefix))?.value}));
                    }
                    // console.log('ind', industriesSearch, industriesIds)
                    localFiltersNew.industries = TB.uniqBy([
                        ...(localFiltersNew.industries || []),
                        ...industriesSearch
                    ], 'id')
                }
                if (key.includes('brands')) {
                    let brandIds = JSON.parse(value) || [];

                    brandIds = TB.filter(brandIds, brand => !TB.isNaN(parseInt(brand)))

                    brandIds = TB.map(brandIds, brand => parseInt(brand));

                    let brandsSearch = TB.filter(availableFilters.brandOptions || [], brand => brandIds.includes(brand.id));

                    localFiltersNew.brands = TB.uniqBy([
                        ...(localFiltersNew.brands || []),
                        ...brandsSearch
                    ], 'id')
                }
                if (key === 'experience') {
                    try {
                        let experience = JSON.parse(value) || {};
                        let validFields = ['junior', 'senior', 'intermediate'];
                        experience = TB.pick(experience, validFields);
                        localFiltersNew.experience = {...localFilters.experience, ...experience}

                    }catch (e) {
                        console.warn('jsonError', e)
                    }
                }

                if (key === 'hired') {
                    localFiltersNew.hired = JSON.parse(value);
                }

                if (key === 'favorited') {
                    localFiltersNew.favorited = JSON.parse(value);
                }
                if (key === 'invited') {
                    localFiltersNew.invited = JSON.parse(value);
                }
                if (key === 'pool_ids') {
                    localFiltersNew.pool_ids = JSON.parse(value);

                    localFiltersNew.pool_ids = TB.filter(localFiltersNew.pool_ids, pool => !TB.isNaN(parseInt(pool)))
                }
                if (key === 'location' && !TB.isNaN(parseInt(JSON.parse(value)))) {
                    localFiltersNew.location = parseInt(JSON.parse(value))
                }

                if (localFiltersNew.location > 3) {
                    if (key === 'latitude' && value !== 'undefined') {
                        if (!TB.isNaN(parseFloat(value))) {
                            localFiltersNew.latitude = parseFloat(JSON.parse(value))
                        }else {
                        localFiltersNew.location = 1;
                        }
                    }
                    if (key === 'longitude' && value !== 'undefined') {
                        if (!TB.isNaN(parseFloat(value))) {
                            localFiltersNew.longitude = parseFloat(JSON.parse(value))
                        }else {
                        localFiltersNew.location = 1;
                        }
                    }
                }

                if (key === 'regions' && localFiltersNew.location === TB.locationRegionalSetting()) {
                    localFiltersNew.regions = JSON.parse(value)
                    localFiltersNew.regions = TB.map(TB.filter(localFiltersNew.regions, region =>  !TB.isNaN(parseInt(region))), region => parseInt(region))
                }
                if (key === 'address' && localFiltersNew.location >= TB.locationProximitySetting()) {
                    localFiltersNew.address = value
                }

            })
            setupParams = async () => {
                await setLocalFilters(localFiltersNew);
                return handleSearch(localFiltersNew)
            };
        }

        const init = async () => {
            await setupParams()
            // await TB.wait(300);
            return setIsInitialized(true)
        }


        init();
    }, [enableTeams, forPosition, searchParams.get('reset')]);

    const selectedLocationLabel = useMemo(() => {
        if (!client_org_regions?.length || !localFilters.location) {
            return ''
        }
        if (localFilters.location === TB.locationRegionalSetting() && localFilters.regions?.length) {
            let regionsLabel = TB.map(localFilters.regions, region => {
                return TB.find(client_org_regions, ['id', parseInt(region)])?.name
            });
            regionsLabel = TB.filter(regionsLabel, label => label?.length)

            if (TB.join(regionsLabel, ', ')?.length > 30) {
                return `(${regionsLabel?.length} Regions`
            }

            return `(${TB.join(regionsLabel, ', ')})`
        }

        return `(${laravel.project_visibility_location_settings[localFilters.location]})`
    }, [client_org_regions, searchParams?.toString(), localFilters?.location, localFilters?.regions]);


    const handleSearch = (onMountFilters, resetPage = false) => {
        // console.log('submitting search')
        // console.log(onMountFilters, 'on mout filters')
        // console.log(onMountFilters, 'on mout filters')

        const filters = {...(onMountFilters || localFilters)};
        // console.log('HANDLE SEARCH', filters)


        // move skillsQueue to skills

        // KEYS TO SEND
        // sort_by: best_match

        // SKILLS - only use important_skills
        // important_skills: 959, 1909, 786, 705
        // nice_to_have_skills: 216
        // very_important_skills: 706

        // INDUSTRIES - only use important_industries
        // nice_to_have_industries: 57
        // very_important_industries: 1
        // important_industries: 2

        // EXPERIENCE
        // accomplished_disciplines: 49, 48, 51, 46, 50, 47, 124
        // advanced_disciplines: 49,48,51,46,50,47,124
        // expert_disciplines: 49,48,51,46,50,47,124

        // NOT SENDING CATEGORIES ANYMORE
        // accomplished_discipline_categories: 11
        // advanced_discipline_categories: 11
        // expert_discipline_categories: 11

        // console.log(localFilters, 'localFilters on submit')

        const params = new URLSearchParams(searchParams);

        if (resetPage) {
            params.delete('page')
        }
        // params.set('sort_by', 'best_match');
        // if (filters.categories.length) {
        //     params.set('accomplished_discipline_categories', JSON.stringify(filters.categories));
        // } else {
        //     params.delete('accomplished_discipline_categories')
        // }

        // delete all _disciplines, then add selected
        params.delete('reset');

        params.delete('experience');

        params.delete('accomplished_disciplines');
        params.delete('advanced_disciplines');
        params.delete('expert_disciplines');
        params.delete('disciplines');

        params.delete('roles');

        params.delete('skills');
        params.delete('nice_to_have_skills');
        params.delete('important_skills');
        params.delete('very_important_skills');

        params.delete('accomplished_project_templates');
        params.delete('advanced_project_templates');
        params.delete('expert_project_templates');
        params.delete('project_templates');

        params.delete('industries');
        params.delete('nice_to_have_industries');
        params.delete('important_industries');
        params.delete('very_important_industries');

        params.delete('brands');

        params.delete('regions')
        params.delete('address')
        params.delete('latitude')
        params.delete('longitude')
        params.delete('pool_ids');


        if (filters.pool_ids?.length) {
            let poolIds = TB.map(filters.pool_ids, pool => pool?.id || pool);
            poolIds = TB.filter(poolIds, pool => !TB.isNaN(parseInt(pool)));

            if (poolIds?.length) {
                params.set('pool_ids', JSON.stringify(poolIds))
            }
        }
        if (filters.brands?.length) {
            let brandIds = TB.map(filters.brands, brand => brand?.id || brand);
            brandIds = TB.filter(brandIds, brand => !TB.isNaN(parseInt(brand)));

            if (brandIds?.length) {
                params.set('brands', JSON.stringify(brandIds))
            }
        }
        if (filters.favorited) {
            params.set('favorited', 1);
        } else {
            params.delete('favorited')
        }
        if (filters.invited) {
            params.set('invited', 1);
        } else {
            params.delete('invited')
        }

        if (filters.hired) {
            params.set('hired', 1);
        } else {
            params.delete('hired')
        }

        if (filters.location) {
            params.set('location', filters.location);
        }

        if (!TB.isNaN(parseFloat(filters.latitude)) && filters.location > 3) {
            params.set('latitude', filters.latitude)
        }
        if (!TB.isNaN(parseFloat(filters.longitude)) && filters.location > 3) {
            params.set('longitude', filters.longitude)
        }


        if (filters.location === TB.locationRegionalSetting() && filters.regions?.length) {
            params.set('regions', JSON.stringify(filters.regions));
        } else {
            params.delete('regions')
        }
        if (filters.location >= TB.locationProximitySetting() && filters.address) {
            params.set('address', filters.address)
        } else {
            params.delete('address')
        }

        if (filters.experience && TB.find(filters.experience, (value, key) => value)) {
            params.set('experience', JSON.stringify(filters.experience));
        }
        if (filters.disciplines?.length) {
            // params.set('accomplished_disciplines', JSON.stringify(filters.disciplines));
            params.set(`disciplines`, JSON.stringify(TB.map(filters.disciplines, 'id')));
        }
        if (filters.roles?.length) {
            // params.set('accomplished_disciplines', JSON.stringify(filters.roles));
            params.set(`roles`, JSON.stringify(TB.map(filters.roles, 'id')));
        }
        if (filters.project_templates?.length) {
            // params.set('accomplished_disciplines', JSON.stringify(filters.disciplines));
            params.set(`project_templates`, JSON.stringify(TB.map(filters.project_templates, 'id')));
        }


        if (filters.skills?.length) {
            if (props.enableImportance) {
                let nice_to_have_skills = TB.filter(filters.skills, skill => parseInt(skill?.importance) === 1)
                if (nice_to_have_skills.length) {
                    params.set('nice_to_have_skills', JSON.stringify(TB.map(nice_to_have_skills, 'id')));
                }
                let important_skills = TB.filter(filters.skills, skill => parseInt(skill?.importance) === 2)
                if (important_skills.length) {
                    params.set('important_skills', JSON.stringify(TB.map(important_skills, 'id')));
                }
                let very_important_skills = TB.filter(filters.skills, skill => parseInt(skill?.importance) === 3);
                if (very_important_skills.length) {
                    params.set('very_important_skills', JSON.stringify(TB.map(very_important_skills, 'id')));
                }
            } else {
                params.set('skills', JSON.stringify(TB.map(filters.skills, 'id')));
            }
            // console.log(TB.map(filters.skillsQueue, 'id'), 'skills ids for params.set')
        }

        // console.log('handleSearchSkills', params?.get('important_skills'))

        if (filters.industries?.length) {
            if (props.enableImportance) {
                let nice_to_have_industries = TB.filter(filters.industries, industry => parseInt(industry?.importance) === 1)
                if (nice_to_have_industries.length) {
                    params.set('nice_to_have_industries', JSON.stringify(TB.map(nice_to_have_industries, 'id')));
                }
                let important_industries = TB.filter(filters.industries, industry => parseInt(industry?.importance) === 2)

                if (important_industries.length) {
                    params.set('important_industries', JSON.stringify(TB.map(important_industries, 'id')));
                }
                let very_important_industries = TB.filter(filters.industries, industry => parseInt(industry?.importance) === 3);
                if (very_important_industries.length) {
                    params.set('very_important_industries', JSON.stringify(TB.map(very_important_industries, 'id')));
                }
            } else {
                params.set('industries', JSON.stringify(TB.map(filters.industries, 'id')));
            }
        }
        // console.log('handleSearchindustries', params?.get('important_industries'), filters.industries)

        // console.log('handleSearch', params);
        if (useLocalStorage) {
            TB.persistStoredBrowseFilters(useLocalStorage, Object.fromEntries(params));
        }
        if (searchParams?.toString() !== params.toString()) {
            console.log('Needtochangelocation')
            return requestAnimationFrame(() => setSearchParams(params, {replace: true}));
        }else {
            console.log('NoNeedtochangelocation')
        }

    }


    const resetSearch = async () => {
        const params = new URLSearchParams(searchParams);
        const currentParams = Object.fromEntries([...searchParams]);
        // console.log(currentParams, 'CURREN TPARMS')

        params.forEach((value, key) => {
            // console.log(value, key, 'reset params');
            params.delete(key);
        });
        let updatedFilters = {...DEFAULT_FILTERS}
        if (forPosition) {
            updatedFilters = {...defaultFilters}
        }
        await setLocalFilters(updatedFilters);
        return handleSearch(updatedFilters)
    }


    return (
        <div className="sidebar-filters">
            <div className="flex-grow flex flex-col gap-2 talent-pool-navbar-nordstrom w-[306px]">
                <div className="flex flex-col">

                    <div className="pt-5">

                        <div className="flex flex-col items-start justify-between gap-1.5 px-4 pb-0">
                            {TB.isString(title) ? (
                                <p className="text-3xl text-wripplePrimaryDarkPurple self-start">
                                    {title}
                                </p>
                            ) : title}
                            {props.children}
                        </div>


                        <Collapse activeKey={collapseActiveKeys} onChange={e => {
                            // console.log('onChangeKey', e);
                            setCollapseActiveKeys(e)
                        }}>
                            {!disabledFilters?.includes("TALENT_TYPES") && (
                                <Collapse.Panel
                                    key="TALENT_TYPE"
                                    header={(
                                        <div
                                            className={TB.mapClass('flex items-center justify-between text-wrippleBlueGray', {
                                                "text-dark": collapseActiveKeys?.includes("TALENT_TYPE")
                                            })}>

                                            <p className="text-base font-bold">Talent Selection</p>
                                            <p className="text-xs">
                                                ({(localFilters?.hired || localFilters.favorited || localFilters.invited || localFilters.pool_ids?.length) ? 'Custom' : 'All Talent'})
                                            </p>
                                        </div>
                                    )}
                                >
                                    <TalentPoolsFilter
                                        value={TB.pick(localFilters, ['hired', 'favorited', 'invited', 'pool_ids'])}
                                        onChange={async (updates) => {
                                            let updatedFilters = {...localFilters, ...updates};
                                            await setLocalFilters(updatedFilters);
                                            return handleSearch(updatedFilters, true)
                                        }}/>

                                </Collapse.Panel>
                            )}

                            {availableFilters.disciplineOptions?.length && !enableTeams && !disabledFilters?.includes("SELECT_ROLE") && (
                                <Collapse.Panel
                                    key="SELECT_ROLE"
                                    header={(
                                        <div
                                            className={TB.mapClass('flex items-center justify-between text-wrippleBlueGray', {
                                                "text-dark": collapseActiveKeys?.includes("SELECT_ROLE")
                                            })}
                                        >
                                            <p className="text-base font-bold">Select Role</p>
                                            {!!localFilters.disciplines?.length && (
                                                <p>({localFilters.disciplines.length})</p>
                                            )}
                                        </div>
                                    )}
                                >
                                    <DisciplinesFilter
                                        value={localFilters.disciplines}
                                        searchEnabled={searchEnabled}
                                        options={availableFilters.disciplineOptions}
                                        onChange={async (updatedDisciplines) => {
                                            let updatedFilters = {...localFilters, disciplines: updatedDisciplines};
                                            await setLocalFilters(updatedFilters);
                                            return handleSearch(updatedFilters, true)
                                        }}/>
                                </Collapse.Panel>
                            )}

                            {forPosition && availableFilters.roleOptions?.length && !enableTeams && !disabledFilters?.includes("SELECT_ROLE") && (
                                <Collapse.Panel
                                    key="SELECT_ROLE"
                                    header={(
                                        <div
                                            className={TB.mapClass('flex items-center justify-between text-wrippleBlueGray', {
                                                "text-dark": collapseActiveKeys?.includes("SELECT_ROLE")
                                            })}
                                        >
                                            <p className="text-base font-bold">Wripple Roles</p>
                                            {!!localFilters.roles?.length && (
                                                <p>({localFilters.roles.length})</p>
                                            )}
                                        </div>
                                    )}
                                >
                                    <RoleTemplatesFilter
                                        value={localFilters.roles}
                                        searchEnabled={searchEnabled}
                                        options={availableFilters.roleOptions}
                                        onChange={async (updatedRoles) => {
                                            let updatedFilters = {...localFilters, roles: updatedRoles};
                                            await setLocalFilters(updatedFilters);
                                            return handleSearch(updatedFilters, true)
                                        }}/>
                                </Collapse.Panel>
                            )}

                            {enableTeams && !disabledFilters?.includes("SELECT_TEMPLATES") && (
                                <Collapse.Panel
                                    key="SELECT_TEMPLATES"
                                    header={(
                                        <div
                                            className={TB.mapClass('flex items-center justify-between text-wrippleBlueGray', {
                                                "text-dark": collapseActiveKeys?.includes("SELECT_TEMPLATES")
                                            })}
                                        >
                                            <p className="text-base font-bold">Select Project Types</p>
                                            {!!localFilters.project_templates?.length && (
                                                <p>({localFilters.project_templates.length})</p>
                                            )}
                                        </div>
                                    )}
                                >
                                    <ProjectTemplatesFilter
                                        value={localFilters.project_templates}
                                        searchEnabled={searchEnabled}
                                        options={availableFilters.projectTemplateOptions}
                                        onChange={async (updatedTemplates) => {
                                            let updatedFilters = {...localFilters, project_templates: updatedTemplates};
                                            await setLocalFilters(updatedFilters);
                                            return handleSearch(updatedFilters, true)
                                        }}/>
                                </Collapse.Panel>
                            )}

                            {availableFilters.skillOptions?.length && !disabledFilters?.includes("REQUIRED_SKILLS") && (
                                <Collapse.Panel
                                    key="REQUIRED_SKILLS"
                                    header={(
                                        <div
                                            className={TB.mapClass('flex items-center justify-between text-wrippleBlueGray', {
                                                "text-dark": collapseActiveKeys?.includes("REQUIRED_SKILLS")
                                            })}
                                        >
                                            <p className="text-base font-bold">Required Skills</p>
                                            {!!localFilters.skills?.length && (
                                                <p>({localFilters.skills.length})</p>
                                            )}
                                        </div>
                                    )}
                                >
                                    <SkillsFilter
                                        selectedDisciplineIds={TB.map(localFilters.disciplines || [], 'id')}
                                        searchEnabled={searchEnabled}
                                        options={availableFilters.skillOptions}
                                        value={localFilters.skills}
                                        enableImportance={props.enableImportance}
                                        onChange={async (updatedSkills) => {
                                            let updatedFilters = {...localFilters, skills: updatedSkills};
                                            await setLocalFilters(updatedFilters);
                                            return handleSearch(updatedFilters, true)
                                        }}/>


                                </Collapse.Panel>
                            )}
                            {!disabledFilters?.includes("EXPERIENCE_LEVELS") && (
                                <Collapse.Panel
                                    key="EXPERIENCE_LEVELS"
                                    header={(
                                        <div
                                            className={TB.mapClass('flex items-center justify-between text-wrippleBlueGray', {
                                                "text-dark": collapseActiveKeys?.includes("EXPERIENCE_LEVELS")
                                            })}
                                        >
                                            <p className="text-base font-bold">Experience Level</p>
                                            {TB.filter(localFilters.experience, (isChecked, type) => isChecked)?.length > 0 && (
                                                <p className="text-xs">({TB.filter(localFilters.experience, (isChecked, type) => isChecked)?.length} Selected)</p>
                                            )}
                                        </div>
                                    )}
                                >
                                    <ExperienceSelectFilter
                                        value={localFilters.experience}
                                        options={availableFilters.experienceOptions}
                                        onChange={async (updatedExp) => {
                                            let updatedFilters = {...localFilters, experience: updatedExp};
                                            await setLocalFilters(updatedFilters);
                                            return handleSearch(updatedFilters, true)
                                        }}
                                    />
                                </Collapse.Panel>
                            )}
                            {!disabledFilters?.includes("LOCATION") && (
                                <Collapse.Panel
                                    key="LOCATION"
                                    header={(
                                        <div
                                            className={TB.mapClass('flex items-center justify-between text-wrippleBlueGray', {
                                                "text-dark": collapseActiveKeys?.includes("LOCATION")
                                            })}
                                        >
                                            <p className="text-base font-bold">Location</p>
                                            {selectedLocationLabel && (
                                                <p className="text-xs">{selectedLocationLabel}</p>
                                            )}
                                        </div>
                                    )}
                                >
                                    <TalentLocationFilter
                                        value={localFilters.location}
                                        options={availableFilters?.locationOptions}
                                        regions={localFilters.regions}
                                        proximity={{full: localFilters.address, address: localFilters.address,latitude: localFilters.latitude, longitude: localFilters.latitude}}
                                        isDisabled={isDisabled}
                                        onChange={async (locationUpdates = {}) => {

                                            let updatedFilters = {...localFilters, ...locationUpdates};
                                            await setLocalFilters(updatedFilters);
                                            if (updatedFilters.location > 3 && (!updatedFilters.latitude || !updatedFilters.longitude)) {
                                                return;
                                            }
                                            return handleSearch(updatedFilters, true)
                                        }}/>
                                </Collapse.Panel>
                            )}
                            {availableFilters.industryOptions?.length && !disabledFilters?.includes("INDUSTRIES") && (
                                <Collapse.Panel
                                    key="INDUSTRIES"
                                    header={(
                                        <div
                                            className={TB.mapClass('flex items-center justify-between text-wrippleBlueGray', {
                                                "text-dark": collapseActiveKeys?.includes("INDUSTRIES")
                                            })}
                                        >
                                            <p className="text-base font-bold">Industries</p>
                                            {!!localFilters.industries?.length && (
                                                <p>({localFilters.industries.length})</p>
                                            )}
                                        </div>
                                    )}
                                >
                                    <IndustriesFilter
                                        value={localFilters.industries}
                                        searchEnabled={searchEnabled}
                                        options={availableFilters.industryOptions}
                                        enableImportance={props.enableImportance}
                                        onChange={async (updatedIndustries) => {
                                            // console.log('updated', updatedIndustries)
                                            let updatedFilters = {...localFilters, industries: updatedIndustries};
                                            await setLocalFilters(updatedFilters);
                                            return handleSearch(updatedFilters, true)
                                        }}/>
                                </Collapse.Panel>
                            )}

                            {availableFilters.brandOptions?.length && !disabledFilters?.includes("BRANDS") && (
                                <Collapse.Panel
                                    key="BRANDS"
                                    header={(
                                        <div
                                            className={TB.mapClass('flex items-center justify-between text-wrippleBlueGray', {
                                                "text-dark": collapseActiveKeys?.includes("BRANDS")
                                            })}
                                        >
                                            <p className="text-base font-bold">Brands</p>
                                            {!!localFilters.brands?.length && (
                                                <p>({localFilters.brands.length})</p>
                                            )}
                                        </div>
                                    )}
                                >
                                    <BrandsFilter
                                        value={localFilters.brands}
                                        searchEnabled={searchEnabled}
                                        options={availableFilters.brandOptions}
                                        enableImportance={false}
                                        onChange={async (updatedBrands) => {
                                            // console.log('updated', updatedBrands)
                                            let updatedFilters = {...localFilters, brands: updatedBrands};
                                            await setLocalFilters(updatedFilters);
                                            return handleSearch(updatedFilters, true)
                                        }}/>
                                </Collapse.Panel>
                            )}


                        </Collapse>
                    </div>
                </div>
                <div className="px-2">
                    <div className="flex justify-between flex-wrap gap-4">
                        {props.footer}
                        <button
                            type="button"
                            className="btn btn-outline-dark"
                            onClick={resetSearch}>
                            Reset
                        </button>
                    </div>
                </div>

                {forPosition && (
                    <div className="mt-6">
                        <ProjectVisibilitySettingsInfo/>
                    </div>
                )}
            </div>
        </div>
    )
}

export default SearchTalentSidebar;

export {
    AllProjectTemplates,
    AllDisciplines
}